import { useState } from "react";
import { Plus, X, Brain, FileText, BarChart3, Lightbulb, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ColonyTemplate {
  id: string;
  name: string;
  icon: typeof Brain;
  color: string;
  description: string;
  defaultDescription: string;
}

const COLONY_TEMPLATES: ColonyTemplate[] = [
  {
    id: "general",
    name: "General",
    icon: Brain,
    color: "ios-blue",
    description: "All-purpose AI assistance for any task",
    defaultDescription: "A versatile colony for general questions, brainstorming, and problem-solving across multiple domains."
  },
  {
    id: "research",
    name: "Research",
    icon: FileText,
    color: "ios-green",
    description: "Deep research and analysis tasks",
    defaultDescription: "Specialized for conducting thorough research, analyzing data, and providing comprehensive insights on complex topics."
  },
  {
    id: "creative",
    name: "Creative",
    icon: Lightbulb,
    color: "ios-yellow",
    description: "Creative writing and ideation",
    defaultDescription: "Focused on creative projects, content creation, storytelling, and innovative thinking."
  },
  {
    id: "analysis",
    name: "Analysis",
    icon: BarChart3,
    color: "ios-purple",
    description: "Data analysis and strategic insights",
    defaultDescription: "Dedicated to analyzing information, generating reports, and providing strategic recommendations."
  }
];

interface QuickCreateColonyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function QuickCreateColonyModal({ isOpen, onClose, onSuccess }: QuickCreateColonyModalProps) {
  const [step, setStep] = useState<'template' | 'details'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<ColonyTemplate | null>(null);
  const [colonyName, setColonyName] = useState("");
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleTemplateSelect = (template: ColonyTemplate) => {
    setSelectedTemplate(template);
    setColonyName("");
    setDescription(template.defaultDescription);
    setStep('details');
  };

  const handleBack = () => {
    if (step === 'details') {
      setStep('template');
      setSelectedTemplate(null);
    }
  };

  const handleCreate = async () => {
    if (!colonyName.trim()) {
      toast({
        title: "Colony name required",
        description: "Please enter a name for your colony.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const colonyId = crypto.randomUUID();
      const existingColonies = JSON.parse(localStorage.getItem('colonies') || '[]');
      const newColony = {
        id: colonyId,
        name: colonyName.trim(),
        description: description.trim(),
        template: selectedTemplate?.id || 'general',
        createdAt: new Date().toISOString()
      };
      localStorage.setItem('colonies', JSON.stringify([...existingColonies, newColony]));
      toast({ title: "Colony created!", description: `"${colonyName}" is ready for your first conversation.` });
      navigate(`/chat/${colonyId}`);
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating colony:', error);
      toast({ title: "Creation failed", description: "Could not create colony. Please try again.", variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setStep('template');
      setSelectedTemplate(null);
      setColonyName("");
      setDescription("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="z-[70] w-[min(560px,94vw)] max-h-[80vh] overflow-auto rounded-2xl border border-white/10 bg-[#111]/95 backdrop-blur p-4">
        <DialogHeader className="relative">
          <div className="flex items-center justify-between mb-2">
            {step === 'details' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="p-2 hover:bg-white/10"
              >
                <ChevronLeft size={18} className="text-white" />
              </Button>
            )}
            <DialogTitle className="ios-headline flex-1 text-center">
              {step === 'template' ? 'Choose Template' : 'Colony Details'}
            </DialogTitle>
            {/* Spacer for layout balance - DialogContent already has a close button */}
            <div className="w-10"></div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {step === 'template' ? (
            <div className="grid grid-cols-2 gap-3">
              {COLONY_TEMPLATES.map((template) => {
                const IconComponent = template.icon;
                return (
                  <Button
                    key={template.id}
                    variant="ghost"
                    className={cn(
                      "h-auto p-4 flex flex-col items-center gap-3 hover:bg-white/5 border border-white/10 bg-[#121212]",
                      "ios-callout text-center min-h-[140px] justify-start"
                    )}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className={cn(
                      "w-16 h-16 rounded-full bg-gradient-to-br from-brain-active to-brain-thinking flex items-center justify-center shrink-0 shadow-lg"
                    )}>
                      <IconComponent size={32} className="text-white" />
                    </div>
                    <div className="flex flex-col items-center gap-2 flex-1">
                      <div className="font-medium text-foreground text-sm">{template.name}</div>
                      <div className="text-xs text-white/60 leading-snug whitespace-normal break-words line-clamp-2 text-center px-1">
                        {template.description}
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-ios-surface-secondary rounded-lg">
                {selectedTemplate && (
                  <>
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      `bg-${selectedTemplate.color}/10`
                    )}>
                      <selectedTemplate.icon size={20} className={`text-${selectedTemplate.color}`} />
                    </div>
                    <div>
                      <div className="ios-subheadline font-medium text-foreground">
                        {selectedTemplate.name} Colony
                      </div>
                      <div className="ios-caption-2 text-muted-foreground">
                        {selectedTemplate.description}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="ios-footnote text-muted-foreground uppercase tracking-wide mb-2 block">
                  Colony Name
                </label>
                <Input
                  value={colonyName}
                  onChange={(e) => setColonyName(e.target.value)}
                  placeholder={`My ${selectedTemplate?.name || 'New'} Colony`}
                  className="h-11 bg-ios-surface border border-ios-separator rounded-lg ios-body"
                  disabled={isCreating}
                />
              </div>

              <div>
                <label className="ios-footnote text-muted-foreground uppercase tracking-wide mb-2 block">
                  Description
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your colony's purpose..."
                  className="min-h-[80px] bg-ios-surface border border-ios-separator rounded-lg ios-body resize-none"
                  disabled={isCreating}
                />
              </div>

              <Button
                onClick={handleCreate}
                disabled={isCreating || !colonyName.trim()}
                className="ios-button-large w-full bg-ios-blue hover:bg-ios-blue/90 text-white border-0"
              >
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={18} className="mr-2" />
                    Create Colony
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}